// src/components/layout/PomodoroBar.tsx
import { useEffect } from 'react';
import { Timer, Coffee, Pause, Play } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { tick, setRunning, switchType, reset, toggleVisible } from 'store/slices/pomodoroSlice';
import { showToast } from 'store/slices/toastSlice';

export default function PomodoroBar() {
  const dispatch = useDispatch();
  const { secs, running, type, count, visible } = useSelector((s: RootState) => s.pomodoro);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => dispatch(tick()), 1000);
    return () => clearInterval(id);
  }, [running, dispatch]);

  useEffect(() => {
    if (secs === 0) {
      if (type === 'work') {
        dispatch(switchType('break'));
        dispatch(showToast('🍅 Pomodoro done! 5 min break.'));
      } else {
        dispatch(switchType('work'));
        dispatch(showToast('☕ Break over! Focus time.'));
      }
    }
  }, [secs, type, dispatch]);

  if (!visible) return null;

  const fmt = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-red-500/8 border-b border-red-500/20 flex-shrink-0">
      <span className="inline-flex items-center gap-1 text-[11px] text-red-400">
        {type === 'work' ? <Timer className="w-3 h-3" strokeWidth={2} /> : <Coffee className="w-3 h-3" strokeWidth={2} />}
        {type === 'work' ? 'Work' : 'Break'}
      </span>
      <span className="text-red-400 text-base font-black tabular-nums min-w-[44px]">{fmt(secs)}</span>

      <button
        onClick={() => dispatch(setRunning(!running))}
        className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 text-[11px] font-bold hover:bg-red-500/25 transition-all"
      >
        {running ? <><Pause className="w-3 h-3" strokeWidth={2} /> Pause</> : <><Play className="w-3 h-3" strokeWidth={2} /> Start</>}
      </button>

      <button
        onClick={() => dispatch(reset())}
        className="px-3 py-1 rounded-lg bg-[var(--om-card)] border border-[var(--om-border)] text-[var(--om-text-faint)] text-[11px] font-bold hover:text-[var(--om-text)] transition-all"
      >
        ↺ Reset
      </button>

      <button
        onClick={() => dispatch(toggleVisible())}
        className="px-3 py-1 rounded-lg bg-[var(--om-card)] border border-[var(--om-border)] text-[var(--om-text-faint)] text-[11px] hover:text-[var(--om-text)] transition-all"
      >
        × Close
      </button>

      <span className="ml-auto text-[11px] text-[var(--om-text-dim)]">{count} sessions today</span>
    </div>
  );
}
